import React, { useState, useEffect } from 'react';
import { Search, Mail, Building, DollarSign, FileText, Calendar, CheckCircle2, XCircle, ChevronLeft, ChevronRight, Send, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from './ui/sonner';
import { getAuthHeaders, checkTokenExpiration } from '../lib/auth';
import { emailTemplates } from '../data/emailTemplates';

const WebLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterConverted, setFilterConverted] = useState('');
  const [filterCampaign, setFilterCampaign] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });

  useEffect(() => {
    fetchLeads();
  }, [page, search, filterConverted, filterCampaign]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.axiosdevelopment.dev';
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
      });
      
      if (search) {
        params.append('search', search);
      }
      
      if (filterConverted) {
        params.append('converted', filterConverted);
      }
      
      if (filterCampaign) {
        params.append('campaignId', filterCampaign);
      }

      const response = await fetch(`${apiUrl}/api/leads?${params}`, {
        headers: getAuthHeaders(),
      });

      if (checkTokenExpiration(response)) {
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch leads');
      }

      if (data.success) {
        setLeads(data.leads);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads', {
        description: error.message || 'Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCampaignName = (campaignId) => {
    if (!campaignId) return 'Direct / Organic';
    const template = emailTemplates.find(t => t.campaignId === campaignId);
    return template ? template.name : campaignId;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Web Leads</h1>
          <p className="text-[#888680]">View and manage leads from your contact form</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-[#3f4816] bg-[#302f2c]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#888680]" />
              <Input
                type="text"
                placeholder="Search by name, email, or company..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10 bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={filterConverted === '' ? 'default' : 'outline'}
                onClick={() => {
                  setFilterConverted('');
                  setPage(1);
                }}
                className={filterConverted === '' ? 'bg-[#d9fb06] text-[#1a1c1b]' : 'border-[#3f4816] text-[#888680]'}
              >
                All Status
              </Button>
              <Button
                variant={filterConverted === 'true' ? 'default' : 'outline'}
                onClick={() => {
                  setFilterConverted('true');
                  setPage(1);
                }}
                className={filterConverted === 'true' ? 'bg-[#d9fb06] text-[#1a1c1b]' : 'border-[#3f4816] text-[#888680]'}
              >
                Converted
              </Button>
              <Button
                variant={filterConverted === 'false' ? 'default' : 'outline'}
                onClick={() => {
                  setFilterConverted('false');
                  setPage(1);
                }}
                className={filterConverted === 'false' ? 'bg-[#d9fb06] text-[#1a1c1b]' : 'border-[#3f4816] text-[#888680]'}
              >
                Not Converted
              </Button>
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-[#3f4816]">
                <span className="text-[#888680] text-sm">Campaign:</span>
                <select
                  value={filterCampaign}
                  onChange={(e) => {
                    setFilterCampaign(e.target.value);
                    setPage(1);
                  }}
                  className="bg-[#1a1c1b] border border-[#3f4816] text-white rounded px-3 py-1.5 text-sm focus:border-[#d9fb06] focus:outline-none"
                >
                  <option value="">All Campaigns</option>
                  {emailTemplates.map((template) => (
                    <option key={template.campaignId} value={template.campaignId}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="border-[#3f4816] bg-[#302f2c]">
        <CardHeader>
          <CardTitle className="text-white">
            Leads ({pagination.total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-[#888680]">Loading leads...</div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-[#888680]">No leads found</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#3f4816]">
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Name</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Email</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Campaign</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Company</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Project Type</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Budget</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Consultation</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Status</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id} className="border-b border-[#3f4816] hover:bg-[#3f4816]/30">
                        <td className="py-3 px-4 text-white">{lead.name}</td>
                        <td className="py-3 px-4">
                          <a href={`mailto:${lead.email}`} className="text-[#d9fb06] hover:underline">
                            {lead.email}
                          </a>
                        </td>
                        <td className="py-3 px-4">
                          {lead.campaignId ? (
                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                              <Send className="h-3 w-3 mr-1" />
                              {getCampaignName(lead.campaignId)}
                            </Badge>
                          ) : (
                            <span className="text-[#888680] text-sm">Direct / Organic</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-[#888680]">{lead.company || '—'}</td>
                        <td className="py-3 px-4 text-[#888680]">{lead.projectType || '—'}</td>
                        <td className="py-3 px-4 text-[#888680]">{lead.budget || '—'}</td>
                        <td className="py-3 px-4">
                          {lead.consultationDate && lead.consultationTime ? (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3 w-3 text-[#888680]" />
                              <div className="text-[#888680] text-sm">
                                <div>{new Date(lead.consultationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                                <div className="flex items-center gap-1 mt-1">
                                  <Clock className="h-3 w-3" />
                                  <span>{lead.consultationTime}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-[#888680] text-sm">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {lead.converted ? (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Converted
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                              <XCircle className="h-3 w-3 mr-1" />
                              New
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-[#888680] text-sm">{formatDate(lead.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#3f4816]">
                  <div className="text-sm text-[#888680]">
                    Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} leads
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="border-[#3f4816] text-[#888680] disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= pagination.pages}
                      className="border-[#3f4816] text-[#888680] disabled:opacity-50"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WebLeads;
